import { z } from "zod";
import { Locale } from "./auth.js";

/*
 * Alerts (docs/alerts.md): rules per organisation, project and server,
 * alerts our API raises from Autopilot data and cloud events, their history,
 * mutes and maintenance windows, Telegram bots configured in the panel,
 * linked chats and each person's subscriptions.
 */

/**
 * What a rule watches:
 * - `agent_down` — the Autopilot agent sent nothing for N minutes;
 * - `server_off` — the provider reports the server not running for N minutes
 *   (not when it was switched off through the panel);
 * - `cpu_high`, `memory_high`, `disk_high` — percent above the threshold for N minutes;
 * - `load_high` — 1-minute load per core above the threshold for N minutes;
 * - `disk_forecast` — a filesystem fills up within the threshold (hours);
 * - `container_down` — a container stopped, restarting or unhealthy for N minutes;
 * - `service_down` — a systemd unit failed for N minutes;
 * - `security_updates` — at least the threshold of security updates pending;
 * - `ssl_expiring` — a certificate (the project's or one Autopilot found) ends within the threshold (days);
 * - `operation_failed` — an operation of the panel ended in an error (one-off);
 * - `budget_threshold` — a project's budget threshold was reached (one-off).
 */
export const AlertKind = z.enum([
  "agent_down",
  "server_off",
  "cpu_high",
  "memory_high",
  "disk_high",
  "load_high",
  "disk_forecast",
  "container_down",
  "service_down",
  "security_updates",
  "ssl_expiring",
  "operation_failed",
  "budget_threshold",
]);
export type AlertKind = z.infer<typeof AlertKind>;

export const AlertSeverity = z.enum(["warn", "critical"]);
export type AlertSeverity = z.infer<typeof AlertSeverity>;

/** Where a rule's values come from: built in, or set for the organisation, the project or the server. */
export const AlertRuleSource = z.enum(["default", "organization", "project", "server"]);
export type AlertRuleSource = z.infer<typeof AlertRuleSource>;

/** A rule as it applies at one level, with where its values come from. */
export const AlertRule = z.object({
  kind: AlertKind,
  enabled: z.boolean(),
  /** Percent, load per core, hours, days or a count (see the kind); null when the kind has none. */
  threshold: z.number().nullable(),
  /** How long the condition lasts before the alert fires; null for kinds that fire at once. */
  durationMinutes: z.number().int().nullable(),
  severity: AlertSeverity,
  source: AlertRuleSource,
  /** What the level above would apply (what a reset gives back). */
  inherited: z
    .object({
      enabled: z.boolean(),
      threshold: z.number().nullable(),
      durationMinutes: z.number().int().nullable(),
      severity: AlertSeverity,
      source: AlertRuleSource,
    })
    .nullable(),
});
export type AlertRule = z.infer<typeof AlertRule>;

export const AlertRuleList = z.object({ rules: z.array(AlertRule) });
export type AlertRuleList = z.infer<typeof AlertRuleList>;

/** `PUT …/rules/:kind`: the rule at this level. */
export const SetAlertRuleRequest = z.object({
  enabled: z.boolean(),
  threshold: z.number().min(0).max(100_000).nullable().default(null),
  durationMinutes: z.number().int().min(0).max(1440).nullable().default(null),
  severity: AlertSeverity,
});
export type SetAlertRuleRequest = z.input<typeof SetAlertRuleRequest>;

export const AlertStatus = z.enum(["firing", "resolved"]);
export type AlertStatus = z.infer<typeof AlertStatus>;

/** Why an alert reached nobody: muted, or inside a maintenance window. */
export const AlertSuppression = z.enum(["muted", "maintenance"]);
export type AlertSuppression = z.infer<typeof AlertSuppression>;

/** One alert: an incident from when it fired until it resolved; one-off events resolve at once. */
export const Alert = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  projectName: z.string(),
  /** Null for alerts about the project (its certificates, operations, budget). */
  serverId: z.uuid().nullable(),
  serverName: z.string().nullable(),
  kind: AlertKind,
  /** What it is about: a filesystem, container, unit, certificate, operation or threshold. */
  subject: z.string(),
  severity: AlertSeverity,
  status: AlertStatus,
  oneOff: z.boolean(),
  /** The measured value (percent, load, hours, days, count) when there is one. */
  value: z.number().nullable(),
  threshold: z.number().nullable(),
  durationMinutes: z.number().int().nullable(),
  startedAt: z.iso.datetime(),
  lastSeenAt: z.iso.datetime(),
  resolvedAt: z.iso.datetime().nullable(),
  /** Set when the alert was not sent to anyone. */
  suppressed: AlertSuppression.nullable(),
  /** Muted now (by the alert, its server or its project) until then. */
  mutedUntil: z.iso.datetime().nullable(),
});
export type Alert = z.infer<typeof Alert>;

export const AlertList = z.object({
  items: z.array(Alert),
  /** Pass as `before` for the next (older) page; null on the last page. */
  next: z.string().nullable(),
});
export type AlertList = z.infer<typeof AlertList>;

export const AlertListQuery = z.object({
  status: z.enum(["firing", "all"]).default("all"),
  serverId: z.uuid().optional(),
  projectId: z.uuid().optional(),
  /** The `next` of the previous page. */
  before: z.uuid().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type AlertListQuery = z.input<typeof AlertListQuery>;

/** «Заглушить»: no messages for this long. */
export const MuteRequest = z.object({
  minutes: z
    .number()
    .int()
    .min(5)
    .max(7 * 24 * 60)
    .default(60),
});
export type MuteRequest = z.input<typeof MuteRequest>;

export const AlertMute = z.object({
  id: z.uuid(),
  /** Null for the whole project. */
  serverId: z.uuid().nullable(),
  serverName: z.string().nullable(),
  /** The one alert muted; null when the server or project is. */
  alertKey: z.string().nullable(),
  kind: AlertKind.nullable(),
  subject: z.string().nullable(),
  until: z.iso.datetime(),
  createdBy: z.string().nullable(),
  createdAt: z.iso.datetime(),
});
export type AlertMute = z.infer<typeof AlertMute>;

export const MaintenanceWindow = z.object({
  id: z.uuid(),
  /** Null for the whole project. */
  serverId: z.uuid().nullable(),
  serverName: z.string().nullable(),
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime(),
  reason: z.string(),
  createdBy: z.string().nullable(),
});
export type MaintenanceWindow = z.infer<typeof MaintenanceWindow>;

export const CreateMaintenanceRequest = z
  .object({
    serverId: z.uuid().nullable().default(null),
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime(),
    reason: z.string().trim().max(200).default(""),
  })
  .refine((w) => new Date(w.endsAt) > new Date(w.startsAt), {
    path: ["endsAt"],
    message: "Must be after the start",
  });
export type CreateMaintenanceRequest = z.input<typeof CreateMaintenanceRequest>;

export const AlertSilences = z.object({
  mutes: z.array(AlertMute),
  windows: z.array(MaintenanceWindow),
});
export type AlertSilences = z.infer<typeof AlertSilences>;

// --- Telegram ---------------------------------------------------------------

/** What BotFather hands out: `<bot id>:<secret>`. */
export const TelegramBotToken = z
  .string()
  .trim()
  .regex(/^\d{5,20}:[A-Za-z0-9_-]{30,64}$/, "Not a bot token such as 123456789:AA…");

/** A Telegram bot of the organisation; its token is never shown again after it was added. */
export const TelegramBot = z.object({
  id: z.uuid(),
  /** `@username` without the at sign, from Telegram. */
  username: z.string(),
  /** The bot's display name in Telegram. */
  name: z.string(),
  /** Last four characters of the token, so administrators can tell tokens apart. */
  tokenHint: z.string(),
  /** Used by every project that has not chosen another bot. */
  isDefault: z.boolean(),
  status: z.enum(["active", "failing"]),
  lastError: z.string().nullable(),
  /** Projects that chose this bot. */
  projects: z.array(z.object({ id: z.uuid(), name: z.string() })),
  /** People and group chats linked to it. */
  linkedPeople: z.number().int(),
  linkedGroups: z.number().int(),
  createdAt: z.iso.datetime(),
});
export type TelegramBot = z.infer<typeof TelegramBot>;

export const AddTelegramBotRequest = z.object({ token: TelegramBotToken });
export type AddTelegramBotRequest = z.infer<typeof AddTelegramBotRequest>;

export const UpdateTelegramBotRequest = z.object({ isDefault: z.literal(true) });
export type UpdateTelegramBotRequest = z.infer<typeof UpdateTelegramBotRequest>;

/** `POST /alerts/bots/:botId/test`: to a linked group chat, or the caller's own chat. */
export const TestTelegramBotRequest = z.object({ targetId: z.uuid().nullable().default(null) });
export const TestTelegramBotResponse = z.object({ sentTo: z.string() });
export type TestTelegramBotResponse = z.infer<typeof TestTelegramBotResponse>;

/** A one-time deep link that links a chat to a bot (`/start <code>`). */
export const TelegramLink = z.object({
  /** `https://t.me/<bot>?start=<code>` (a person) or `?startgroup=<code>` (a group chat). */
  url: z.string(),
  /** What to send the bot by hand when the link does not open: `/start <code>`. */
  command: z.string(),
  botUsername: z.string(),
  expiresAt: z.iso.datetime(),
});
export type TelegramLink = z.infer<typeof TelegramLink>;

/** A group chat linked to a project. */
export const AlertGroupChat = z.object({
  id: z.uuid(),
  title: z.string(),
  botUsername: z.string(),
  minSeverity: AlertSeverity,
  linkedAt: z.iso.datetime(),
  linkedBy: z.string().nullable(),
});
export type AlertGroupChat = z.infer<typeof AlertGroupChat>;

/** `GET /projects/:projectId/alerts/channels` — which bot sends the project's alerts, and its group chats. */
export const ProjectAlertChannels = z.object({
  /** The bot the project chose; null: the organisation default. */
  botId: z.uuid().nullable(),
  /** The bot that sends now (the choice, else the default); null without any bot. */
  bot: z.object({ id: z.uuid(), username: z.string(), isDefault: z.boolean() }).nullable(),
  /** Bots to choose from (administrators). */
  bots: z.array(z.object({ id: z.uuid(), username: z.string(), isDefault: z.boolean() })),
  groups: z.array(AlertGroupChat),
});
export type ProjectAlertChannels = z.infer<typeof ProjectAlertChannels>;

export const SetProjectBotRequest = z.object({ botId: z.uuid().nullable() });
export type SetProjectBotRequest = z.infer<typeof SetProjectBotRequest>;

export const LinkGroupRequest = z.object({ minSeverity: AlertSeverity.default("warn") });
export type LinkGroupRequest = z.input<typeof LinkGroupRequest>;

// --- A person's notifications ------------------------------------------------

/** `HH:MM`, 24 hours. */
export const ClockTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use HH:MM");

export const QuietHours = z.object({
  start: ClockTime,
  end: ClockTime,
  /** IANA time zone, e.g. `Europe/Moscow`. */
  timeZone: z.string().min(1).max(64),
  /** Critical alerts still arrive during quiet hours. */
  allowCritical: z.boolean().default(true),
});
export type QuietHours = z.infer<typeof QuietHours>;

/** One project a person follows: every server, or only some. */
export const AlertSubscription = z.object({
  projectId: z.uuid(),
  /** Null: every server of the project (and its project-wide alerts). */
  serverIds: z.array(z.uuid()).max(200).nullable(),
});
export type AlertSubscription = z.infer<typeof AlertSubscription>;

export const NotificationPreferences = z.object({
  /** Null: every project the person can see. */
  projects: z.array(AlertSubscription).max(200).nullable(),
  /** Alerts below it are not sent (`critical`: only critical ones). */
  minSeverity: AlertSeverity,
  quietHours: QuietHours.nullable(),
  telegram: z.boolean(),
  panel: z.boolean(),
  /** The language of the person's Telegram messages (their account's language). */
  language: Locale,
});
export type NotificationPreferences = z.infer<typeof NotificationPreferences>;

/** `GET /account/notifications` — the caller's settings and their Telegram links. */
export const AccountNotifications = z.object({
  preferences: NotificationPreferences,
  /** Never saved: the defaults apply. */
  defaults: z.boolean(),
  bots: z.array(
    z.object({
      id: z.uuid(),
      username: z.string(),
      isDefault: z.boolean(),
      /** The caller's chat with this bot, once linked. */
      chat: z.object({ id: z.uuid(), title: z.string(), linkedAt: z.iso.datetime() }).nullable(),
    }),
  ),
});
export type AccountNotifications = z.infer<typeof AccountNotifications>;

export const LinkTelegramRequest = z.object({ botId: z.uuid() });
export type LinkTelegramRequest = z.infer<typeof LinkTelegramRequest>;

/** In-panel notifications: alerts sent to the caller. */
export const AlertInboxItem = z.object({
  id: z.uuid(),
  phase: z.enum(["fired", "resolved"]),
  createdAt: z.iso.datetime(),
  readAt: z.iso.datetime().nullable(),
  alert: Alert,
});
export type AlertInboxItem = z.infer<typeof AlertInboxItem>;

export const AlertInbox = z.object({ items: z.array(AlertInboxItem), unread: z.number().int() });
export type AlertInbox = z.infer<typeof AlertInbox>;

export const MarkInboxRequest = z.object({ ids: z.array(z.uuid()).max(200).optional() });
export type MarkInboxRequest = z.infer<typeof MarkInboxRequest>;
